/**
 * What one webhook is: where to call, when, and with what headers.
 *
 * The events are a checklist rather than a free list, because the backend's
 * `EventsEnum` is closed -- twelve of them -- and a webhook listening for an
 * event that does not exist is one that never fires and never says why.
 *
 * **Testing before saving is the point of the test call.** A webhook fails
 * silently by design: the row is still created, the call is still made, and
 * nothing on screen says the other end refused it. So the form offers one
 * made-up call and shows the answer in full.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { WebhookEvent } from '@/api/types'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'
import { Textarea } from '@/shared/ui/textarea'
import { useTestWebhook, type WebhookDraft, type WebhookMethod } from './queries'

/** In the backend's own order: rows, then comments, then fields, then views. */
export const WEBHOOK_EVENTS = [
  'rows.created',
  'rows.updated',
  'rows.deleted',
  'row_comment.created',
  'row_comment.updated',
  'row_comment.deleted',
  'field.created',
  'field.updated',
  'field.deleted',
  'view.created',
  'view.updated',
  'view.deleted',
] as const satisfies readonly WebhookEvent[]

const METHODS = [
  'POST',
  'GET',
  'PUT',
  'PATCH',
  'DELETE',
] as const satisfies readonly WebhookMethod[]

/** The headers as the text somebody edits: one `Name: value` per line. */
export function headersToText(headers: Record<string, string>): string {
  return Object.entries(headers)
    .map(([name, value]) => `${name}: ${value}`)
    .join('\n')
}

/**
 * The headers a block of text describes.
 *
 * A line with no colon is dropped rather than stored under an empty name: the
 * endpoint would refuse it, and refusing the whole save because of one stray
 * line is worse than ignoring the line. Everything after the first colon is the
 * value, because a value may contain one -- a URL usually does.
 */
export function headersFromText(text: string): Record<string, string> {
  const headers: Record<string, string> = {}
  for (const line of text.split('\n')) {
    const at = line.indexOf(':')
    if (at === -1) continue
    const name = line.slice(0, at).trim()
    if (name === '') continue
    headers[name] = line.slice(at + 1).trim()
  }
  return headers
}

export function WebhookForm({
  tableId,
  draft,
  onChange,
}: {
  tableId: number
  draft: WebhookDraft
  onChange: (next: WebhookDraft) => void
}) {
  const { t } = useTranslation()
  const test = useTestWebhook(tableId)
  const [headerText, setHeaderText] = useState(() => headersToText(draft.headers))

  const set = <K extends keyof WebhookDraft>(key: K, value: WebhookDraft[K]): void => {
    onChange({ ...draft, [key]: value })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="webhook-name">{t('webhook.name')}</Label>
        <Input
          id="webhook-name"
          value={draft.name}
          onChange={(event) => {
            set('name', event.target.value)
          }}
        />
      </div>

      <div className="flex gap-2">
        <div className="flex w-28 flex-col gap-1.5">
          <Label htmlFor="webhook-method">{t('webhook.method')}</Label>
          <NativeSelect
            id="webhook-method"
            value={draft.request_method}
            onChange={(event) => {
              set('request_method', event.target.value as WebhookMethod)
            }}
          >
            {METHODS.map((method) => (
              <NativeSelectOption key={method} value={method}>
                {method}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="webhook-url">{t('webhook.url')}</Label>
          <Input
            id="webhook-url"
            type="url"
            value={draft.url}
            onChange={(event) => {
              set('url', event.target.value)
            }}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="webhook-all-events"
          size="sm"
          checked={draft.include_all_events}
          onCheckedChange={(next) => {
            set('include_all_events', next)
          }}
        />
        <Label htmlFor="webhook-all-events">{t('webhook.allEvents')}</Label>
      </div>

      {/* The list stays on screen while "everything" is on, greyed rather than
          gone: it is what the webhook falls back to the moment that is turned
          off, and hiding it would make turning it off look like it did nothing. */}
      <fieldset
        disabled={draft.include_all_events}
        className="flex flex-col gap-1 disabled:opacity-50"
      >
        <legend className="mb-1 text-sm">{t('webhook.events')}</legend>
        {WEBHOOK_EVENTS.map((event) => (
          <div key={event} className="flex items-center gap-2">
            <Checkbox
              id={`webhook-event-${event}`}
              checked={draft.events.includes(event)}
              onCheckedChange={(checked) => {
                set(
                  'events',
                  checked === true
                    ? [...draft.events, event]
                    : draft.events.filter((one) => one !== event),
                )
              }}
            />
            <Label htmlFor={`webhook-event-${event}`} className="font-mono text-xs">
              {event}
            </Label>
          </div>
        ))}
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="webhook-headers">{t('webhook.headers')}</Label>
        <Textarea
          id="webhook-headers"
          rows={3}
          placeholder="Authorization: Bearer ..."
          value={headerText}
          onChange={(event) => {
            setHeaderText(event.target.value)
            set('headers', headersFromText(event.target.value))
          }}
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="webhook-user-field-names"
          size="sm"
          checked={draft.use_user_field_names}
          onCheckedChange={(next) => {
            set('use_user_field_names', next)
          }}
        />
        <Label htmlFor="webhook-user-field-names">{t('webhook.userFieldNames')}</Label>
      </div>

      <div className="flex flex-col gap-2 border-t pt-3">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={draft.url.trim() === '' || test.isPending}
            onClick={() => {
              test.mutate({
                url: draft.url,
                // The first event it listens for, or rows being created: a test
                // has to name one, and this is the one somebody setting up a
                // webhook is nearly always waiting to see.
                eventType: draft.events[0] ?? 'rows.created',
              })
            }}
          >
            {t('webhook.test')}
          </Button>
          {test.isError && (
            <span className="text-sm text-destructive">{t('webhook.testFailed')}</span>
          )}
        </div>
        {test.data !== undefined && (
          <pre
            aria-label={t('webhook.testResult')}
            className="max-h-40 overflow-auto rounded bg-muted p-2 font-mono text-xs"
          >
            {test.data.response ?? test.data.request ?? ''}
          </pre>
        )}
      </div>
    </div>
  )
}
